import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { contactsApi } from '../../api/api';
import { AdminSelect, AdminModalClose } from '../components/AdminUI';

const PAGE_SIZE_OPTIONS = [6, 12, 18, 24];

const STATUS_STYLES = {
  new: { bg: '#fef3c7', text: '#d97706', label: 'New', icon: 'fas fa-envelope' },
  read: { bg: '#dbeafe', text: '#2563eb', label: 'Read', icon: 'fas fa-envelope-open' },
  replied: { bg: '#dcfce7', text: '#16a34a', label: 'Replied', icon: 'fas fa-check-circle' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.new;
  return (
    <span
      className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      <i className={`${s.icon} text-xs`}></i>{s.label}
    </span>
  );
};

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatDateTime = (d) =>
  new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

/** Inbox card — website/admin style like Events list cards */
const ContactCard = ({ contact, onOpen, onDelete }) => {
  const isNew = contact.status === 'new';
  const initial = (contact.firstName || '?').charAt(0).toUpperCase();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`group bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer ${
        isNew ? 'border-orange-200' : 'border-gray-100'
      }`}
      style={isNew ? { background: 'linear-gradient(to bottom right, #fffbeb, #ffffff)' } : undefined}
    >
      <div className="p-4 md:p-5 flex items-start gap-3 md:gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
          style={isNew
            ? { background: '#ffedd5', color: '#ea580c' }
            : { background: '#f3f4f6', color: '#6b7280' }}
        >
          {initial}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className={`font-bold text-sm md:text-base truncate ${isNew ? 'text-gray-900' : 'text-gray-800'}`}>
              {contact.firstName} {contact.lastName}
            </h3>
            <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{formatDate(contact.createdAt)}</span>
          </div>

          <p className={`text-sm truncate mb-2 ${isNew ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
            {contact.subject || 'No subject'}
          </p>
          <p className="text-xs text-gray-500 leading-relaxed mb-3" style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {contact.message}
          </p>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={contact.status} />
              {contact.phone && (
                <span className="text-xs text-gray-400">
                  <i className="fas fa-phone mr-1"></i>{contact.phone}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold"
              style={{ background: '#fef2f2', color: '#dc2626' }}
              title="Delete"
            >
              <i className="fas fa-trash mr-1"></i>Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageModal = ({ contact, onClose, onUpdateStatus, updating }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 className="font-bold text-gray-800 text-lg">Message</h3>
        <AdminModalClose onClick={onClose} />
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Name</p>
            <p className="font-semibold text-gray-800">{contact.firstName} {contact.lastName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Phone</p>
            <p className="text-gray-700">{contact.phone || '—'}</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Subject</p>
          <p className="text-gray-700">{contact.subject || '—'}</p>
        </div>

        <div>
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Message</p>
          <div className="bg-gray-50 rounded-xl p-4 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
            {contact.message}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Received</p>
            <p className="text-sm text-gray-600">{formatDateTime(contact.createdAt)}</p>
          </div>
          <StatusBadge status={contact.status} />
        </div>

        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Update Status</p>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(STATUS_STYLES).map(([key, s]) => (
              <button
                key={key}
                type="button"
                onClick={() => onUpdateStatus(contact.id, key)}
                disabled={updating || contact.status === key}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: contact.status === key ? s.bg : '#f3f4f6',
                  color: contact.status === key ? s.text : '#6b7280',
                  border: contact.status === key ? `1.5px solid ${s.text}30` : '1.5px solid transparent',
                }}
              >
                {updating ? <i className="fas fa-spinner fa-spin text-xs"></i> : <i className={`${s.icon} text-xs`}></i>}
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ConfirmDialog = ({ name, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={onCancel}>
    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#fee2e2' }}>
        <i className="fas fa-trash text-xl" style={{ color: '#ef4444' }}></i>
      </div>
      <h4 className="font-bold text-gray-800 mb-1">Delete Message?</h4>
      <p className="text-sm text-gray-500 mb-4">
        Message from <strong>{name}</strong> will be permanently removed.
      </p>
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} className="flex-1 py-2 border border-gray-200 rounded-xl text-gray-600 font-semibold text-sm">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={loading}
          className="flex-1 py-2 admin-btn-danger rounded-xl font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>} Delete
        </button>
      </div>
    </div>
  </div>
);

const ContactsAdmin = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [statusFilter, setStatusFilter] = useState('new');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(6);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await contactsApi.getAll({});
      if (res.success) setContacts(res.data || []);
      else setError(res.message);
    } catch { setError('Failed to load contacts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, perPage]);

  const markAsRead = useCallback(async (id) => {
    try {
      const res = await contactsApi.updateStatus(id, 'read');
      if (!res.success) return;
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'read' } : c)));
      setSelected((prev) => (prev?.id === id ? { ...prev, status: 'read' } : prev));
    } catch { /* silent */ }
  }, []);

  const openMessage = (contact) => {
    setSelected(contact);
    if (contact.status === 'new') markAsRead(contact.id);
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(q) ||
        (c.subject || '').toLowerCase().includes(q) ||
        (c.message || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q)
      );
    });
  }, [contacts, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredItems.slice(start, start + perPage);
  }, [filteredItems, currentPage, perPage]);

  const rangeStart = filteredItems.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, filteredItems.length);
  const newCount = contacts.filter((c) => c.status === 'new').length;

  const handleUpdateStatus = async (id, status) => {
    setUpdating(true);
    try {
      const res = await contactsApi.updateStatus(id, status);
      if (!res.success) throw new Error(res.message);
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
      if (selected?.id === id) setSelected((prev) => ({ ...prev, status }));
      showToast(`✅ Marked as ${status}`);
    } catch (err) { alert(err.message || 'Update failed'); }
    finally { setUpdating(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await contactsApi.delete(deleteTarget.id);
      if (!res.success) throw new Error(res.message);
      setDeleteTarget(null);
      if (selected?.id === deleteTarget.id) setSelected(null);
      showToast('🗑️ Deleted');
      load();
    } catch (err) { alert(err.message || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">{toast}</div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">📬 Contact Inbox</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading
              ? 'Loading…'
              : `${filteredItems.length} total · showing ${rangeStart}–${rangeEnd}${newCount > 0 ? ` · ${newCount} new` : ''}`}
          </p>
        </div>
        <button type="button" onClick={load} className="admin-btn-primary px-5 py-2.5 text-sm">
          <i className="fas fa-sync-alt"></i> Refresh
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 mb-5">
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <div className="admin-search-wrap flex-1 min-w-0">
            <i className="fas fa-search admin-search-icon" aria-hidden="true"></i>
            <input
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, subject, phone or message..."
              className="admin-search-input"
            />
            {search && (
              <button
                type="button"
                className="admin-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            )}
          </div>
          <AdminSelect
            aria-label="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: '', label: 'All Status' },
              { value: 'new', label: 'New' },
              { value: 'read', label: 'Read' },
              { value: 'replied', label: 'Replied' },
            ]}
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 whitespace-nowrap">Per page</span>
            <AdminSelect
              aria-label="Items per page"
              value={perPage}
              onChange={setPerPage}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button type="button" onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center">
          <i className="fas fa-spinner fa-spin text-2xl" style={{ color: '#f97316' }}></i>
          <p className="text-gray-500 text-sm mt-3">Loading messages...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-2xl border border-dashed border-gray-200">
          <i className="fas fa-inbox text-4xl mb-3" style={{ color: '#d1d5db' }}></i>
          <p className="text-gray-500 font-medium">No messages found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:gap-4">
            {paginatedItems.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                onOpen={() => openMessage(c)}
                onDelete={() => setDeleteTarget(c)}
              />
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-gray-500 leading-none">
              Page {currentPage} of {totalPages} · {filteredItems.length} message{filteredItems.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === 1 ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                <i className="fas fa-chevron-left mr-1"></i>Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => totalPages <= 7 || p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => (
                  p === '…' ? (
                    <span key={`e${idx}`} className="px-1 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setCurrentPage(p)}
                      className="w-9 h-9 rounded-lg text-sm font-semibold"
                      style={p === currentPage
                        ? { background: 'linear-gradient(to right, #f97316, #dc2626)', color: '#fff' }
                        : { background: '#fff', color: '#4b5563', border: '1px solid #e5e7eb' }}
                    >
                      {p}
                    </button>
                  )
                ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-white border border-gray-200 text-gray-700 hover:border-orange-400'}`}
              >
                Next<i className="fas fa-chevron-right ml-1"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {selected && (
        <MessageModal
          contact={selected}
          onClose={() => setSelected(null)}
          onUpdateStatus={handleUpdateStatus}
          updating={updating}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          name={`${deleteTarget.firstName} ${deleteTarget.lastName}`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default ContactsAdmin;
